/**
 * OriginalsView: the capture PC's originals (admin only: the Rigs console's
 * Originals tab, after Review). Contract: docs/ingest/19-capture-pc-originals.md.
 *
 * The consent promise keeps no other copy of a guest's photos once their
 * files are online, and no archive. The originals are Xangle's dataset
 * folders on the capture PC's datasets drive. After the event a person
 * deletes them in Xangle (or Explorer) from this list and empties the
 * Recycle Bin; the uploader then checks the drive and the row says Gone.
 *
 * What it shows (getOriginals, computed on the server from the records):
 *   - when the uploader last checked the drive, and its Recycle Bin;
 *   - Safe to delete: the folders to delete now (only those the uploader's
 *     latest check of the known folder saw on the drive), with the steps;
 *     any other safe one is listed apart, not to delete yet;
 *   - Needs you: folders a person looks at first (the uploader skipped
 *     them, two folders name one capture, a folder that moved), and any gone
 *     before it was safe;
 *   - Not tied to a guest: folders fired outside the Suite, with their time,
 *     photo count and dates (tie by the 14th day after the event; still
 *     untied on the 30th, they move to Safe to delete), and two actions: Tie
 *     to a registration, Not a guest's capture;
 *   - Keep for now and Gone, folded;
 *   - the job's switch "originals may go once their photos are in the cloud".
 *
 * Reads only through the admin callables (rigv2Callables.js), and the roster
 * (live) while the tie dialog is open. Nothing here deletes a file.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./rigv2-review.css";
import "./rigv2-originals.css";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { rigv2 } from "./rigv2Callables.js";
import { useModalDialog } from "./dialogFocus.js";
import {
  acceptRootInput, actionErrorText, agedView, agoText, canRelease, dayText, driveChip, elapsedSince, folderListText, loadStamped, lostText, recycleBinText,
  releaseInput, rosterMatches, sectionsOf, seenOf, shortTime, viewGate, whyText,
} from "./originalsModel.js";

/** Every 5 minutes while the tab is open and shown (and Refresh any time): the uploader checks the drive about as often. */
export const REFRESH_MS = 5 * 60_000;
/** How often the tab looks at how old its answer is (agedView), and at once when it is shown again. */
export const AGE_TICK_MS = 15_000;

/* ── pieces ── */

function Chip({ tone, children }) {
  return <span className={`rv-status ${tone}`}><i />{children}</span>;
}

function OriginalRow({ row, now = 0, actions = null, alarm = false }) {
  const [chip, tone] = alarm ? ["Needs you", "needs"] : driveChip(row);
  const who = row.kind === "capture"
    ? [row.displayId ? `#${String(row.displayId).replace(/^#/, "")}` : "", row.name ?? ""].filter(Boolean).join(" ")
    : "";
  const facts = row.kind === "untied"
    ? [
      row.createdAt ? `Taken ${shortTime(row.createdAt)}${now > row.createdAt ? ` (${agoText(row.createdAt, now)})` : ""}` : "",
      typeof row.photoCount === "number" ? `${row.photoCount} photos` : "",
    ].filter(Boolean).join(" · ")
    : "";
  // A tie: a folder tied to no one that staff tied (its decision), or a capture relinked from one (its tie).
  const tie = row.kind === "untied" ? (row.decision?.kind === "tie" ? row.decision : null) : row.tie;
  const notGuestWhy = row.kind === "untied" && row.decision?.kind === "not_guest" ? row.decision.reason : null;
  return (
    <li className="og-row">
      <div className="og-main">
        <span className="og-folder mono">{row.folder ?? "folder not known"}</span>
        {row.sn && <span className="og-sn mono">{row.sn}</span>}
        {who && <span className="og-who">{who}</span>}
        {facts && <span className="og-facts">{facts}</span>}
      </div>
      <div className="og-why">
        {alarm && row.gone ? lostText(row) : whyText(row)}
        {row.gone && row.goneSince ? <span className="og-sub"> Gone since {shortTime(row.goneSince)}.</span> : null}
        {tie ? (
          <span className="og-sub">
            {" "}Tied to {tie.displayId ? `#${tie.displayId}` : "a registration"}{tie.reason ? ` (${tie.reason})` : ""}
            {tie.matches === false ? ". The capture's guest is not that registration: check it." : "."}
          </span>
        ) : null}
        {notGuestWhy ? <span className="og-sub"> ({notGuestWhy})</span> : null}
      </div>
      <div className="og-side">
        <Chip tone={tone}>{chip}</Chip>
        {actions}
      </div>
    </li>
  );
}

function Section({ title, count, children, sub = null, open = true, tools = null }) {
  if (!open) {
    return (
      <details className="og-section og-folded">
        <summary><span className="og-title">{title}</span> <span className="og-count">{count}</span></summary>
        {sub && <p className="rv-muted">{sub}</p>}
        {children}
      </details>
    );
  }
  return (
    <section className="og-section">
      <div className="og-head">
        <h3 className="og-title">{title} <span className="og-count">{count}</span></h3>
        {tools}
      </div>
      {sub && <div className="rv-muted og-subline">{sub}</div>}
      {children}
    </section>
  );
}

/* ── the screen (pure: exported for the SSR test) ── */

export function OriginalsScreen({
  view, loading = false, error = "", notice = "", busy = {}, copied = false, copyFallback = "",
  onRefresh = () => {}, onToggleMayGo = () => {}, onAcceptRoot = () => {}, onTie = () => {}, onNotGuest = () => {}, onUndo = () => {}, onCopy = () => {},
  onRelease = () => {},
}) {
  const s = useMemo(() => sectionsOf(view), [view]);
  if (!view) {
    return (
      <div className="rigv2-review rigv2-originals">
        <div className="rv-empty">
          <p className="rv-empty-title">Originals on the capture PC</p>
          <p>{error || (loading ? "Loading…" : "Nothing to show yet.")}</p>
          {!loading && <button type="button" className="rigv2-btn small" onClick={onRefresh}>Try again</button>}
        </div>
      </div>
    );
  }
  const inv = view.inventory;
  const now = view.now;
  const [binText, binTone] = recycleBinText(inv?.recycleBin ?? null);
  const dates = view.dates ?? {};
  // Only folders the uploader's latest trusted check saw on the drive are to be deleted.
  const toDelete = s.toDelete;
  return (
    <div className="rigv2-review rigv2-originals">
      <div className="rv-head">
        <div className="rv-head-left">
          <span className="rv-eyebrow">Capture PC</span>
          <h2 className="og-h2">Originals</h2>
          <span className="rv-sub">
            {inv
              ? `Last checked by the uploader ${shortTime(inv.checkedAt)} (${agoText(inv.checkedAt, now)}): ${inv.total} folder${inv.total === 1 ? "" : "s"} in ${inv.rootPath}.`
              : "The uploader has not reported the capture PC's folders yet (capture-uploader 0.4.0 or later does)."}
          </span>
        </div>
        <div className="rv-toolbar">
          <button type="button" className="rigv2-btn small" onClick={onRefresh} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
        </div>
      </div>

      {error && <div className="rv-banner error">{error}</div>}
      {notice && <div className="rv-banner">{notice}</div>}
      {inv?.rootMismatch && (
        <div className="rv-banner error">
          <span>
            The uploader now reads {inv.rootPath}, not {inv.knownRootPath} as before (or that folder was made again). Until you say
            this is right, nothing is marked gone and nothing is listed to delete. If the originals really moved there, press Use this
            folder.
          </span>
          <button type="button" className="rigv2-btn small" disabled={Boolean(busy.root)} onClick={onAcceptRoot}>Use this folder</button>
        </div>
      )}
      {inv && !inv.rootMismatch && inv.confirmed === false && (inv.unseen ?? 0) > 0 && (
        <div className="rv-banner">
          <span>
            {inv.unseen} of this job&apos;s {inv.unseen === 1 ? "captures is" : "captures are"} not in the uploader&apos;s folder ({inv.rootPath}),
            and no check ever saw {inv.unseen === 1 ? "it" : "them"} there: {inv.unseen === 1 ? "it" : "they"} may be on another drive, so
            {inv.unseen === 1 ? " it is" : " they are"} not marked gone. If this folder is where this job&apos;s originals were, press Confirm.
          </span>
          <button type="button" className="rigv2-btn small" disabled={Boolean(busy.root)} onClick={onAcceptRoot}>Confirm</button>
        </div>
      )}
      {inv?.stale && !inv.rootMismatch && !inv.agedHere && (
        <div className="rv-banner">The last check is over 30 minutes old: the uploader is not running, or cannot reach the Suite. Nothing is listed to delete until a fresh check; Gone is only as of that one.</div>
      )}
      {inv?.stale && !inv.rootMismatch && inv.agedHere && (
        <div className="rv-banner">
          The check shown is over 30 minutes old now: nothing is listed to delete until a fresh one. Press Refresh; if that does not help, this
          screen cannot reach the Suite or the uploader is not running.
        </div>
      )}
      {inv && !inv.place?.mine && inv.place?.owner && (
        <div className="rv-banner">
          {inv.place.released
            ? "This job released the capture PC's drive for another job: nothing on it is listed to delete here."
            : `The capture PC's drive is reported for job ${inv.place.owner} now: nothing on it is listed to delete here.`}
        </div>
      )}
      {(inv?.labelDoubt?.length ?? 0) > 0 && (
        <div className="rv-banner error">
          The label of {inv.labelDoubt.length === 1 ? "folder" : "folders"} {inv.labelDoubt.join(", ")} on the drive cannot be read: it may be any
          capture's. Until it is fixed (or looked at and moved), no capture is listed to delete or marked gone.
        </div>
      )}
      {inv && <div className={`rv-banner og-bin ${binTone}`}>{binText}</div>}

      <div className="rv-strip og-strip">
        <span className="rv-stat"><span className="v">{toDelete.length}</span><span className="l">To delete</span></span>
        <span className="rv-stat"><span className="v">{s.needs.length}</span><span className="l">Needs you</span></span>
        <span className="rv-stat"><span className="v">{s.untied.length}</span><span className="l">Tied to no one</span></span>
        <span className="rv-stat"><span className="v">{s.keep.length}</span><span className="l">Keep for now</span></span>
        <span className="rv-stat"><span className="v">{s.gone.length}</span><span className="l">Gone</span></span>
      </div>

      <Section
        title="Safe to delete" count={toDelete.length}
        tools={toDelete.length > 0 && (
          <button type="button" className="rigv2-btn small" onClick={() => onCopy(folderListText(toDelete))}>{copied ? "Copied" : "Copy the folder names"}</button>
        )}
        sub={(
          <ol className="og-steps">
            <li>On the capture PC, open Xangle (or File Explorer at {inv?.rootPath ?? "the datasets folder"}).</li>
            <li>Delete each folder listed here as Delete it. Only these: a folder not on this list stays.</li>
            <li>Empty the Recycle Bin.</li>
            <li>Within a few minutes the uploader checks the drive, and each row says Gone (press Refresh).</li>
          </ol>
        )}
      >
        {/* The names the clipboard could not take: only while they are still the list to delete. */}
        {copyFallback && toDelete.length > 0 && copyFallback === folderListText(toDelete) && (
          <p className="rv-muted og-subline">The folder names: {copyFallback.split("\n").join(", ")}</p>
        )}
        {toDelete.length === 0 ? <p className="rv-muted">Nothing to delete right now.</p> : (
          <ul className="og-list">{toDelete.map((r) => (
            <OriginalRow key={`${r.kind}:${r.folder}:${r.sn ?? ""}`} row={r} now={now}
              actions={r.kind === "untied" && r.decision?.kind === "not_guest" ? (
                <button type="button" className="rigv2-btn small" disabled={Boolean(busy[r.folder])} onClick={() => onUndo(r)}>Keep it</button>
              ) : null} />
          ))}</ul>
        )}
        {s.unchecked.length > 0 && (
          <>
            <p className="rv-muted og-subline">
              {s.unchecked.length} more {s.unchecked.length === 1 ? "is" : "are"} safe to delete, but the uploader has not seen {s.unchecked.length === 1 ? "it" : "them"} on
              the drive in a check this screen trusts. Not to delete until {s.unchecked.length === 1 ? "it shows" : "they show"} Delete it.
            </p>
            <ul className="og-list">{s.unchecked.map((r) => <OriginalRow key={`c:${r.kind}:${r.folder}:${r.sn ?? ""}`} row={r} now={now} />)}</ul>
          </>
        )}
      </Section>

      {s.needs.length > 0 && (
        <Section title="Needs you" count={s.needs.length} sub="Look at these on the capture PC before deleting anything of them. Ask Claude if unsure.">
          <ul className="og-list">{s.needs.map((r) => <OriginalRow key={`n:${r.kind}:${r.folder}:${r.sn ?? ""}`} row={r} now={now} alarm={Boolean(r.gone)} />)}</ul>
        </Section>
      )}

      <Section
        title="Not tied to a guest" count={s.untied.length}
        sub={dates.tieBy && !dates.problem
          ? `Fired outside the Suite (no label). Tie each to a registration by ${dayText(dates.tieBy)}${dates.tieByPassed ? " (that date has passed)" : ""}; any still untied on ${dayText(dates.deleteFrom)} moves to Safe to delete.`
          : `Fired outside the Suite (no label). ${dates.problem ?? "The job's dates cannot be read"}: nothing moves to Safe to delete by date until they can.`}
      >
        {s.untied.length === 0 ? <p className="rv-muted">None.</p> : (
          <ul className="og-list">{s.untied.map((r) => (
            <OriginalRow key={`u:${r.folder}`} row={r} now={now} actions={r.decision ? (
              <button type="button" className="rigv2-btn small" disabled={Boolean(busy[r.folder]) || !r.present} onClick={() => onUndo(r)}>Undo</button>
            ) : (
              <>
                <button type="button" className="rigv2-btn small" disabled={Boolean(busy[r.folder]) || !r.present} onClick={() => onTie(r)}>Tie to a registration</button>
                <button type="button" className="rigv2-btn small rv-danger" disabled={Boolean(busy[r.folder]) || !r.present} onClick={() => onNotGuest(r)}>Not a guest's capture</button>
              </>
            )} />
          ))}</ul>
        )}
        {s.untied.some((r) => r.decision?.kind === "tie") && (
          <p className="rv-footnote">
            A tied folder is relinked on the capture PC with the rig bridge, with Ivin (XR Brooklyn playbook 5.1: arm the
            registration, Start without firing, then type bind &lt;folder&gt; &lt;new SN&gt;). The uploader then sees its label and
            uploads it like any capture. Relink before the job's capture freeze: a frozen job refuses Start.
          </p>
        )}
      </Section>

      <Section title="Keep for now" count={s.keep.length} open={false} sub="These must stay on the drive for now.">
        <ul className="og-list">{s.keep.map((r) => <OriginalRow key={`k:${r.kind}:${r.folder}:${r.sn ?? ""}`} row={r} now={now} />)}</ul>
      </Section>

      <Section title="Gone" count={s.gone.length} open={false} sub="Not on the drive in the uploader's latest check.">
        <ul className="og-list">{s.gone.map((r) => <OriginalRow key={`g:${r.kind}:${r.folder}:${r.sn ?? ""}`} row={r} now={now} />)}</ul>
      </Section>

      <section className="og-section">
        <label className="og-switch">
          <input type="checkbox" checked={Boolean(view.settings?.originalsMayGo)} disabled={Boolean(busy.mayGo)}
            onChange={(e) => onToggleMayGo(e.target.checked)} />
          <span>
            <strong>Originals may go once their photos are in the cloud</strong>
            <span className="rv-muted og-subline">
              Off: an original is safe to delete once its files are online (the model approved, or photos only). On: as soon as
              its photos are verified in the cloud.{view.settings?.at ? ` Last changed ${shortTime(view.settings.at)}.` : ""}
            </span>
          </span>
        </label>
      </section>

      {inv?.place?.mine && (
        <section className="og-section">
          <div className="og-head">
            <h3 className="og-title">The drive, for the next job</h3>
            <button type="button" className="rigv2-btn small" disabled={!canRelease(view) || Boolean(busy.release)} onClick={onRelease}>Release the drive</button>
          </div>
          <div className="rv-muted og-subline">
            The capture PC's datasets folder is this job's until every capture and folder above is Gone; only then can another
            job's uploader report it. {canRelease(view) ? "Everything is gone: it can be released." : "Not yet: some are not gone."}
          </div>
        </section>
      )}
    </div>
  );
}

/* ── the dialogs ── */

function Dialog({ label, onClose, children }) {
  const frameRef = useRef(null);
  useModalDialog(frameRef);
  return (
    <div className="rigv2-modal-scrim rv-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rv-dialog" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={frameRef}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
        {children}
      </div>
    </div>
  );
}

/** Tie a folder to a registration: search by number or name, a reason, confirm. Pure but for its own fields: exported for the test. */
export function TieDialog({ folder, roster, loading = false, busy = false, error = "", onConfirm, onClose }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const matches = useMemo(() => rosterMatches(roster, query), [roster, query]);
  return (
    <Dialog label="Tie to a registration" onClose={onClose}>
      <p className="rv-dialog-title">Tie {folder.folder} to a registration</p>
      <p className="rv-dialog-sub">
        Only when you are sure whose capture it is (the paper ledger line with this folder name, say). The registration gets a
        deletion hold, and the folder stays on the drive until it is relinked on the capture PC and uploaded.
      </p>
      <label className="rv-field">
        <span className="rv-label">Pass number or name</span>
        <input className="rv-input" value={query} autoFocus onChange={(e) => { setQuery(e.target.value); setPicked(null); }} />
      </label>
      {loading && <p className="rv-hint">Loading the registrations…</p>}
      <div className="og-matches" role="listbox" aria-label="Registrations">
        {matches.map((r) => (
          <button key={r.id} type="button" role="option" aria-selected={picked?.id === r.id}
            className={`og-match ${picked?.id === r.id ? "picked" : ""}`} onClick={() => setPicked(r)}>
            {r.displayId ? `#${String(r.displayId).replace(/^#/, "")} ` : ""}{r.name || "Unnamed"}
          </button>
        ))}
        {query.trim() && !loading && matches.length === 0 && <p className="rv-hint">No registration matches.</p>}
      </div>
      <label className="rv-field">
        <span className="rv-label">Why (for example: ledger line 12)</span>
        <input className="rv-input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </label>
      {error && <p className="rv-note">{error}</p>}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={!picked || busy}
          onClick={() => onConfirm(picked, reason.trim())}>{busy ? "Saving…" : picked ? `Tie to #${picked.displayId ?? "?"}` : "Pick a registration"}</button>
      </div>
    </Dialog>
  );
}

export function NotGuestDialog({ folder, busy = false, error = "", onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog label="Not a guest's capture" onClose={onClose}>
      <p className="rv-dialog-title">{folder.folder} is not a guest's capture?</p>
      <div className="rv-dialog-lines">
        <span>A test fire, or a capture of nobody. Nobody can be sent it, and it is listed to delete unless something else holds it.</span>
        <span>You can take it back while the folder is still on the drive.</span>
      </div>
      <label className="rv-field">
        <span className="rv-label">Why (optional)</span>
        <input className="rv-input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </label>
      {error && <p className="rv-note">{error}</p>}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="rigv2-btn rv-danger" disabled={busy} onClick={() => onConfirm(reason.trim())}>{busy ? "Saving…" : "Not a guest's capture"}</button>
      </div>
    </Dialog>
  );
}

function ReleaseDialog({ busy = false, error = "", onConfirm, onClose }) {
  return (
    <Dialog label="Release the drive" onClose={onClose}>
      <p className="rv-dialog-title">Release the capture PC's drive for another job?</p>
      <div className="rv-dialog-lines">
        <span>Every original of this job is gone from it. The next job's uploader can then report it, and this tab lists nothing on it to delete.</span>
        <span>This cannot be taken back from here.</span>
      </div>
      {error && <p className="rv-note">{error}</p>}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={busy} onClick={onConfirm}>{busy ? "Saving…" : "Release the drive"}</button>
      </div>
    </Dialog>
  );
}

function MayGoDialog({ on, busy = false, error = "", onConfirm, onClose }) {
  return (
    <Dialog label="Originals may go" onClose={onClose}>
      <p className="rv-dialog-title">{on ? "Let originals go once their photos are in the cloud?" : "Wait for the files to be online again?"}</p>
      <div className="rv-dialog-lines">
        {on
          ? <span>Every capture whose photos are verified in the cloud becomes safe to delete, model or not. The cloud copy is then the only one until the model is made.</span>
          : <span>An original becomes safe to delete again only once its model is approved, or photos only is chosen.</span>}
      </div>
      {error && <p className="rv-note">{error}</p>}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={busy} onClick={onConfirm}>{busy ? "Saving…" : on ? "Let them go" : "Wait"}</button>
      </div>
    </Dialog>
  );
}

/* ── the container ── */

function RosterTie({ jobId, folder, busy, error, onConfirm, onClose }) {
  const roster = useFirestoreCollection(`jobs/${jobId}/rosterEntries`);
  return <TieDialog folder={folder} roster={roster.data} loading={roster.loading} busy={busy} error={error} onConfirm={onConfirm} onClose={onClose} />;
}

export default function OriginalsView({ jobId, load = rigv2.getOriginals, settings = rigv2.setOriginalsSettings, decide = rigv2.decideOriginalsFolder }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState({});
  const [dialog, setDialog] = useState(null);
  const [dialogError, setDialogError] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyFallback, setCopyFallback] = useState("");
  // Which answers may be shown: only the newest, and none while a change is being sent (viewGate).
  const gate = useRef(viewGate()).current;
  // When the answer shown was asked for (loadStamped): it ages on this page's own clocks from then (agedView).
  const stamp = useRef(null);
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    const mine = gate.startLoad();
    if (mine === null) return;
    setLoading(true);
    try {
      const { view: v, stamp: asked } = await loadStamped(load, { jobId });
      if (gate.accept(mine)) {
        stamp.current = asked;
        setView(v);
        setError("");
      }
    } catch (e) {
      if (gate.accept(mine)) setError(/Requires role/.test(String(e?.message)) ? "Only an admin can see the originals." : "Could not load the originals. Check the connection and try again.");
    } finally {
      if (gate.isLatest(mine)) setLoading(false);
    }
  }, [jobId, load, gate]);

  useEffect(() => {
    refresh();
    const t = setInterval(() => { if (typeof document === "undefined" || !document.hidden) refresh(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // The answer ages even when nothing new comes (a lost connection, a tab left open or asleep): looked at every
  // AGE_TICK_MS, and at once when the tab is shown again (then a fresh one is asked for too).
  useEffect(() => {
    const tick = () => setTick((n) => n + 1);
    const t = setInterval(tick, AGE_TICK_MS);
    const onShow = () => {
      tick();
      if (!document.hidden) refresh();
    };
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("focus", tick);
    };
  }, [refresh]);
  // And exactly when the check shown grows old (a timer late in a hidden tab is caught by the ones above).
  useEffect(() => {
    const left = view?.inventory && !view.inventory.stale && typeof view.inventory.freshForMs === "number"
      ? view.inventory.freshForMs - elapsedSince(stamp.current) : null;
    if (left === null || !(left > 0)) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), left + 50);
    return () => clearTimeout(t);
  }, [view]);
  const shown = view ? agedView(view, elapsedSince(stamp.current)) : null;

  const run = async (key, fn, ok) => {
    setBusy((b) => ({ ...b, [key]: true }));
    setDialogError("");
    // Whatever the answer (even one lost on the way), what was shown may no longer hold (a folder kept, the switch off):
    // nothing is listed to delete until a fresh answer comes after the change settles; no refresh lands meanwhile.
    gate.beginChange();
    setView(null);
    let failure = null;
    try {
      await fn();
      setDialog(null);
    } catch (e) {
      failure = actionErrorText(e);
    } finally {
      gate.endChange();
    }
    await refresh();
    if (failure) {
      if (dialog) setDialogError(failure);
      else setError(failure);
    } else if (ok) setNotice(ok);
    setBusy((b) => ({ ...b, [key]: false }));
  };

  // The list as it stands now (aged to this moment), never the one last drawn: an old one copies nothing.
  const copy = async () => {
    const list = folderListText(sectionsOf(view ? agedView(view, elapsedSince(stamp.current)) : null).toDelete);
    setTick((n) => n + 1);
    if (!list) return;
    try {
      await navigator.clipboard.writeText(list);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Shown beside the list, and only while it is still the list to delete.
      setCopyFallback(list);
    }
  };

  return (
    <>
      <OriginalsScreen
        view={shown} loading={loading} error={error} notice={notice} busy={busy} copied={copied} copyFallback={copyFallback}
        onRefresh={refresh}
        onToggleMayGo={(on) => { setDialogError(""); setDialog({ kind: "mayGo", on }); }}
        onRelease={() => { setDialogError(""); setDialog({ kind: "release" }); }}
        onAcceptRoot={() => {
          const input = acceptRootInput(jobId, view);
          if (input) run("root", () => settings(input), "The new folder is the one checked from now on.");
        }}
        onTie={(row) => { setDialogError(""); setDialog({ kind: "tie", row }); }}
        onNotGuest={(row) => { setDialogError(""); setDialog({ kind: "notGuest", row }); }}
        onUndo={(row) => run(row.folder, () => decide({ jobId, folder: row.folder, action: "undo", decisionId: row.decision?.id }),
          `${row.folder}: decision taken back.`)}
        onCopy={copy}
      />
      {/* The dialogs take the review screen's tokens and button sizes too. */}
      {dialog && (
        <div className="rigv2-review rigv2-originals og-dialogs">
          {dialog.kind === "tie" && (
            <RosterTie jobId={jobId} folder={dialog.row} busy={Boolean(busy[dialog.row.folder])} error={dialogError}
              onClose={() => setDialog(null)}
              onConfirm={(entry, reason) => run(dialog.row.folder,
                () => decide({ jobId, folder: dialog.row.folder, action: "tie", rosterEntryId: entry.id, reason, seen: seenOf(dialog.row) }),
                `${dialog.row.folder} is tied to #${entry.displayId ?? "?"}. Relink it on the capture PC.`)} />
          )}
          {dialog.kind === "notGuest" && (
            <NotGuestDialog folder={dialog.row} busy={Boolean(busy[dialog.row.folder])} error={dialogError}
              onClose={() => setDialog(null)}
              onConfirm={(reason) => run(dialog.row.folder,
                () => decide({ jobId, folder: dialog.row.folder, action: "not_guest", reason, seen: seenOf(dialog.row) }),
                `${dialog.row.folder}: decision saved (not a guest's capture).`)} />
          )}
          {dialog.kind === "release" && (
            <ReleaseDialog busy={Boolean(busy.release)} error={dialogError} onClose={() => setDialog(null)}
              onConfirm={() => {
                const input = releaseInput(jobId, view);
                if (input) run("release", () => settings(input), "The drive is released for the next job.");
              }} />
          )}
          {dialog.kind === "mayGo" && (
            <MayGoDialog on={dialog.on} busy={Boolean(busy.mayGo)} error={dialogError} onClose={() => setDialog(null)}
              onConfirm={() => run("mayGo", () => settings({ jobId, originalsMayGo: dialog.on }))} />
          )}
        </div>
      )}
    </>
  );
}
